/**
 * pageContext: 画面上の構造化データを AI に渡すための抽出ユーティリティ。
 *
 * 命名規約 (全画面共通 — 違反禁止):
 *   data-entity="<snake_case_entity_name>"   API リソース名と一致 (例: "property", "contract")
 *   data-id="<stable_id>"                    そのエンティティの ID
 *   data-field="<snake_case_field_name>"     BE スキーマのフィールド名と一致 (例: "property_name")
 *   data-label="<日本語ラベル>"              任意。指定が無い場合は近傍の dt/label/aria-* から推定
 *   data-value-raw="<内部値>"                任意。enum 等の内部値を明示したい時 (例: "ACTIVE")
 *
 * 各 page は root に data-entity + data-id を持つ要素を1つ用意し、
 * 配下に data-field を持つ要素を必要数配置する。
 *
 * 設計原則:
 * - エンティティ名/フィールド名をハードコードしない。DOM を走査するだけ。
 * - 入れ子の別 entity 配下の field は親 entity に含めない (closest で判定)。
 * - 出力は token 効率優先。1 値の最大長は MAX_VALUE_LENGTH で切り詰め。
 */

export interface PageContextField {
  /** API スキーマのフィールド名 (snake_case) */
  field: string;
  /** 画面に表示されているラベル (日本語) */
  label: string;
  /** 表示値 (textContent) */
  value: string;
  /** 内部値 (enum 等)。data-value-raw が指定された時のみ */
  raw?: string;
  /** Select / Autocomplete などの選択肢一覧。
   *  data-options 属性に JSON 配列を埋めると抽出される。
   *  AI が「グループの選択肢は?」「建蔽率は何が選べる?」のような質問に答えられるようにするため。 */
  options?: Array<Record<string, unknown>>;
}

export interface PageContextCollectionItem {
  /** 行内の data-field 要素から抽出したフィールド配列 */
  fields: PageContextField[];
}

export interface PageContextCollection {
  /** 配列の API スキーマ名 (snake_case)。例: "roads" "lots" */
  name: string;
  /** 画面に表示されている見出し */
  label: string;
  /** 行数 */
  count: number;
  /** data-collection-item 配下を1行ずつ抽出した配列 */
  items: PageContextCollectionItem[];
}

export interface PageContextEntity {
  /** API リソース名 (snake_case) */
  entity: string;
  /** リソース ID */
  id: string;
  /** data-field 要素から抽出したフィールド配列 (collection 配下は含まない) */
  fields: PageContextField[];
  /** data-collection 要素から抽出した配列フィールド (接道情報、地番境界 等) */
  collections?: PageContextCollection[];
}

export interface PageContext {
  /** pathname + search */
  route: string;
  /** 画面上の data-entity 要素を出現順に列挙 */
  entities: PageContextEntity[];
}

const MAX_VALUE_LENGTH = 200;

const normalizeText = (raw: string): string => {
  const trimmed = raw.trim().replace(/\s+/g, " ");
  return trimmed.length > MAX_VALUE_LENGTH
    ? trimmed.slice(0, MAX_VALUE_LENGTH) + "..."
    : trimmed;
};

const extractText = (el: Element | null): string => {
  if (!el) return "";
  return normalizeText(el.textContent ?? "");
};

const findLabel = (fieldEl: HTMLElement): string => {
  // 1. data-label が最優先 (明示指定)
  const own = fieldEl.getAttribute("data-label");
  if (own) return own;

  // 2. <dt> 隣接 <dd>
  if (fieldEl.tagName === "DD") {
    const prev = fieldEl.previousElementSibling;
    if (prev && prev.tagName === "DT") return extractText(prev);
  }

  // 3. label[for] 関連
  if (fieldEl.id) {
    const escaped =
      typeof CSS !== "undefined" && CSS.escape
        ? CSS.escape(fieldEl.id)
        : fieldEl.id.replace(/"/g, '\\"');
    const label = document.querySelector(`label[for="${escaped}"]`);
    if (label) return extractText(label);
  }

  // 4. 親が <label>
  const parentLabel = fieldEl.closest("label");
  if (parentLabel && parentLabel !== fieldEl) return extractText(parentLabel);

  // 5. aria-label / aria-labelledby
  const ariaLabel = fieldEl.getAttribute("aria-label");
  if (ariaLabel) return ariaLabel;
  const ariaLabelledby = fieldEl.getAttribute("aria-labelledby");
  if (ariaLabelledby) {
    const referenced = document.getElementById(ariaLabelledby);
    if (referenced) return extractText(referenced);
  }

  return "";
};

/**
 * data-field 要素の配下に入力欄 (input / textarea / select) があれば、
 * その現在値 (DOM プロパティ) を取り出す。
 *
 * 必要な理由: React の controlled component では <input value={...}> はプロパティに
 * セットされるだけで HTML attribute としては書かれず、textContent にも出ない。
 * そのため textContent だけでは「画面に何のフィールドがあるか」しか分からず、
 * 「入っているか / 何が入っているか」が取れない。
 *
 * Select / radio / checkbox 等で「表示テキストと内部値が違う」ケースは、画面側で
 * data-value-raw を明示することで raw 側に内部値を入れる。ここでは入力中の生値だけ拾う。
 */
const findInputValue = (fieldEl: HTMLElement): string | null => {
  const input = fieldEl.querySelector<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("input, textarea, select");
  if (!input) return null;
  // type=hidden な MUI Select の内部 input も拾ってしまうが、Select は
  // 画面側で data-value-raw を付ける運用 (現実装) なので問題なし。
  // 未入力の input.value は "" を返す。
  return typeof input.value === "string" ? input.value : "";
};

export const getPageContext = (
  root: Document | HTMLElement = typeof document !== "undefined" ? document : (null as unknown as Document)
): PageContext => {
  const route =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "";

  if (!root) return { route, entities: [] };

  const entityEls = Array.from(
    root.querySelectorAll<HTMLElement>("[data-entity][data-id]")
  );

  const entities: PageContextEntity[] = entityEls.map((entityEl) => {
    const entity = entityEl.getAttribute("data-entity") ?? "";
    const id = entityEl.getAttribute("data-id") ?? "";

    const fieldEls = Array.from(
      entityEl.querySelectorAll<HTMLElement>("[data-field]")
    );

    // 同じ field 名の data-field 要素が重複する場合は最後 (より深いネストの方) を採用する。
    // 理由: shared-components の Custom* が自動的に root に data-field を出すため、
    //  画面側で手動ラッパー <div data-field=...> も併用すると親子で重複する。
    //  深い方 (子要素) には options / value-raw 等のより詳細な属性が乗りやすいため、子優先。
    const allFields: PageContextField[] = fieldEls
      // 入れ子の別 entity 配下にある field は親 entity から除外
      // また data-collection 配下にある field は collections 側に拾うので entity.fields から除外
      .filter(
        (fieldEl) =>
          fieldEl.closest("[data-entity][data-id]") === entityEl &&
          !fieldEl.closest("[data-collection]")
      )
      .map((fieldEl) => {
        const field = fieldEl.getAttribute("data-field") ?? "";
        const label = findLabel(fieldEl);
        const rawAttr = fieldEl.getAttribute("data-value-raw");

        // value 採用順:
        //   1. data-value-raw あり → textContent (表示文字列)。raw は別途記録。
        //   2. data-value-raw なし
        //      a. 配下に input/textarea/select があれば .value プロパティ (空文字も保持)
        //      b. それも無ければ textContent (表示専用要素)
        let value: string;
        if (rawAttr !== null) {
          value = extractText(fieldEl);
        } else {
          const inputValue = findInputValue(fieldEl);
          value = inputValue !== null ? inputValue : extractText(fieldEl);
        }

        const result: PageContextField = { field, label, value };
        if (rawAttr) result.raw = rawAttr;

        // data-options が指定されていれば JSON.parse して options に入れる。
        // パース失敗時は無視 (AI 側で「選択肢情報なし」として扱う)。
        const optionsAttr = fieldEl.getAttribute("data-options");
        if (optionsAttr) {
          try {
            const parsed = JSON.parse(optionsAttr);
            if (Array.isArray(parsed)) {
              result.options = parsed as Array<Record<string, unknown>>;
            }
          } catch {
            // ignore invalid JSON
          }
        }

        return result;
      });

    // 同一 field の重複除去 (最後出 = より深いネストを採用)
    const fieldsByField = new Map<string, PageContextField>();
    for (const f of allFields) {
      if (!f.field) continue;
      fieldsByField.set(f.field, f);
    }
    const fields: PageContextField[] = Array.from(fieldsByField.values());

    // 配列フィールド (data-collection) を抽出
    const collectionEls = Array.from(
      entityEl.querySelectorAll<HTMLElement>("[data-collection]")
    ).filter(
      // 入れ子の別 entity 配下の collection は除外。
      (el) => el.closest("[data-entity][data-id]") === entityEl
    );

    const collections: PageContextCollection[] = collectionEls.map(
      (collEl) => {
        const name = collEl.getAttribute("data-collection") ?? "";
        const label =
          collEl.getAttribute("data-collection-label") ?? findLabel(collEl);

        const itemEls = Array.from(
          collEl.querySelectorAll<HTMLElement>("[data-collection-item]")
        ).filter((itemEl) => itemEl.closest("[data-collection]") === collEl);

        const items: PageContextCollectionItem[] = itemEls.map((itemEl) => {
          const itemFieldEls = Array.from(
            itemEl.querySelectorAll<HTMLElement>("[data-field]")
          ).filter(
            (fieldEl) => fieldEl.closest("[data-collection-item]") === itemEl
          );

          // 行内も最後出を採用 (Custom* 自動付与 + 手動ラッパー併用ケース)
          const itemFieldsByField = new Map<string, PageContextField>();
          for (const fieldEl of itemFieldEls) {
            const field = fieldEl.getAttribute("data-field") ?? "";
            if (!field) continue;
            const labelText = findLabel(fieldEl);
            const rawAttr = fieldEl.getAttribute("data-value-raw");
            let value: string;
            if (rawAttr !== null) {
              value = extractText(fieldEl);
            } else {
              const inputValue = findInputValue(fieldEl);
              value = inputValue !== null ? inputValue : extractText(fieldEl);
            }
            const fieldResult: PageContextField = {
              field,
              label: labelText,
              value,
            };
            if (rawAttr) fieldResult.raw = rawAttr;
            const optionsAttr = fieldEl.getAttribute("data-options");
            if (optionsAttr) {
              try {
                const parsed = JSON.parse(optionsAttr);
                if (Array.isArray(parsed)) {
                  fieldResult.options = parsed as Array<
                    Record<string, unknown>
                  >;
                }
              } catch {
                // ignore
              }
            }
            itemFieldsByField.set(field, fieldResult);
          }

          return { fields: Array.from(itemFieldsByField.values()) };
        });

        // 件数は items.length を優先、明示指定 (data-collection-count) があればそれを尊重
        const countAttr = collEl.getAttribute("data-collection-count");
        const count =
          countAttr !== null && !Number.isNaN(Number(countAttr))
            ? Number(countAttr)
            : items.length;

        return { name, label, count, items };
      }
    );

    return collections.length > 0
      ? { entity, id, fields, collections }
      : { entity, id, fields };
  });

  return { route, entities };
};

/** 画面の主エンティティ(最初に現れる data-entity 要素)を取得 */
export const getPrimaryEntity = (ctx: PageContext): PageContextEntity | null =>
  ctx.entities[0] ?? null;
